// src/pages/customers/CreateCustomerPage.tsx
import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import Modal from "@/components/shared/ui/Modal";
import CreateCustomerForm, {
  type UpsertCustomer,
} from "@/components/customers/forms/CreateCustomerForm";

import { useCustomersPage } from "./useCustomersPage";
import { useNavigate } from "react-router-dom";

export default function CreateCustomerPage() {
  const page = useCustomersPage();
  const navigate = useNavigate();

  const handleCreateSubmit = (payload: UpsertCustomer) => {
    page.onCreate(payload, {
      onSuccess: () => {
        // After successful creation, go back to list
        navigate("/customers");
      },
    });
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            Create Customer
          </h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6">
       <CreateCustomerForm
        onSubmit={handleCreateSubmit}
        submitting={page.createSubmitting}
        onCancel={() => navigate("/customers")}
        errorMessage={page.createError}
        />
      </div>

      {/* Email already belongs to a user → offer to attach instead of duplicating */}
      <Modal
        title="This email already has an account"
        open={!!page.attachCandidate}
        onClose={page.onCancelAttach}
      >
        {page.attachCandidate ? (
          <div className="space-y-4">
            <p className="text-sm leading-6 text-gray-600">
              <span className="font-semibold text-gray-900">
                {page.attachCandidate.lookup.fullName}
              </span>{" "}
              already has a login
              {page.attachCandidate.lookup.roleName ? (
                <>
                  {" "}
                  as{" "}
                  <span className="font-medium text-gray-900">
                    {page.attachCandidate.lookup.roleName}
                  </span>
                </>
              ) : null}
              . Add a customer profile to this existing account — one login that
              also acts as a customer — instead of creating a duplicate?
            </p>

            {page.attachError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {page.attachError}
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={page.onCancelAttach}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={page.attachSubmitting}
                onClick={page.onConfirmAttach}
                className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
              >
                {page.attachSubmitting ? "Attaching…" : "Attach as customer"}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </AdminLayout>
  );
}
